import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { Check, Loader2, AlertTriangle } from "lucide-react";
import { http } from "@/lib/api";
import { useCart } from "@/lib/cart";
import { LogoMark } from "@/components/site/Logo";

export default function PaymentSuccess() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const { clear } = useCart();
  const [state, setState] = useState(sessionId ? "checking" : "error");

  useEffect(() => {
    if (!sessionId) return undefined;
    let tries = 0;
    let timer;
    let alive = true;
    const poll = async () => {
      try {
        const { data } = await http.get(`/payments/status/${sessionId}`);
        if (!alive) return;
        if (data?.payment_status === "paid") { setState("paid"); clear(); return; }
        if (data?.status === "expired") { setState("error"); return; }
      } catch {
        if (!alive) return;
      }
      tries += 1;
      if (tries >= 10) setState("pending");
      else timer = setTimeout(poll, 2000);
    };
    poll();
    return () => { alive = false; clearTimeout(timer); };
  }, [sessionId, clear]);

  return (
    <main className="flex min-h-screen items-center justify-center px-5" data-testid="payment-success-page">
      <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} className="w-full max-w-lg rounded-[2rem] border border-fg/10 bg-[var(--ink-2)] p-10 text-center">
        <LogoMark className="mx-auto h-12 w-12" />
        {state === "checking" && (<><Loader2 className="mx-auto mt-8 h-8 w-8 animate-spin text-[var(--volt)]" /><h1 className="mt-4 font-display text-3xl">Verifica del pagamento…</h1></>)}
        {state === "paid" && (
          <>
            <span className="mx-auto mt-8 flex h-16 w-16 items-center justify-center rounded-full bg-[var(--volt)] text-[var(--ink)]"><Check className="h-8 w-8" /></span>
            <h1 className="mt-6 font-display text-4xl" data-testid="payment-success-title">Grazie per l'ordine!</h1>
            <p className="mt-3 text-fg2">Pagamento confermato. Riceverai la ricevuta via email da Stripe.</p>
          </>
        )}
        {(state === "pending" || state === "error") && (
          <>
            <AlertTriangle className="mx-auto mt-8 h-8 w-8 text-[var(--flare)]" />
            <h1 className="mt-4 font-display text-3xl" data-testid="payment-status-unknown">{state === "pending" ? "Pagamento in elaborazione" : "Pagamento non trovato"}</h1>
            <p className="mt-3 text-fg2">Se hai completato il pagamento, controlla la tua email. Per dubbi scrivici dal modulo di richiesta.</p>
          </>
        )}
        <Link to="/" data-testid="payment-back-home" className="mt-8 inline-block rounded-full bg-[var(--paper)] px-7 py-3 font-semibold text-[var(--ink)]">Torna al negozio</Link>
      </motion.div>
    </main>
  );
}
